import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Clock, Heart, Trophy, Dumbbell, Bike, Waves, Flame, Zap, TrendingUp, Activity, BrainCircuit, Sparkles, RefreshCw, BarChart2, ChevronDown, ChevronUp, Search, X, Wind, Cloud, CloudRain, Sun, MapPin, Share2, Eye, ShieldAlert, Navigation } from 'lucide-react';
import DOMPurify from 'dompurify';
import { formatLocalDate } from '../utils/date';
import ActivityDetails from './ActivityDetails';
import DailyFeedbackWidget from './DailyFeedbackWidget';
import UnifiedActivityCard from './UnifiedActivityCard';
import PowerCurveWidget from './PowerCurveWidget';
import WPrimeWidget from './WPrimeWidget';
import ZonesDistributionWidget from './ZonesDistributionWidget';
import { PoweredByStrava, ViewOnStravaButton } from './StravaAssets';

const AiLoadingState = () => {
  const phrases = [
    "Procesando métricas biométricas...",
    "Analizando picos de intensidad cardiaca...",
    "Correlacionando fatiga y recuperación...",
    "Estimando ventana de supercompensación...",
    "Generando informe fisiológico PRO..."
  ];
  const [phraseIdx, setPhraseIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIdx(prev => (prev + 1) % phrases.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem', gap: '1.5rem', background: 'rgba(0,0,0,0.2)', borderRadius: '12px' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', width: '60px', height: '60px', borderRadius: '50%', background: 'conic-gradient(from 0deg, transparent, #8b5cf6, #00e5ff, transparent)', animation: 'spin 2s linear infinite' }} />
        <div style={{ width: '54px', height: '54px', borderRadius: '50%', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, boxShadow: 'inset 0 0 10px rgba(139, 92, 246, 0.5)' }}>
          <BrainCircuit size={24} style={{ color: '#00e5ff', animation: 'pulse 1.5s ease-in-out infinite' }} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={14} style={{ color: '#8b5cf6' }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 600, background: 'linear-gradient(90deg, #8b5cf6, #00e5ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', transition: 'all 0.3s' }}>
            {phrases[phraseIdx]}
          </span>
        </div>
        <div style={{ width: '180px', height: '4px', background: 'rgba(255,255,255,0.05)', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{ height: '100%', background: 'linear-gradient(90deg, #8b5cf6, #00e5ff)', borderRadius: '2px', animation: 'progress-indeterminate 1.5s ease-in-out infinite' }} />
        </div>
      </div>
      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        @keyframes pulse { 50% { opacity: 0.5; transform: scale(0.95); } }
        @keyframes progress-indeterminate { 0% { width: 0%; transform: translateX(-100%); } 50% { width: 70%; transform: translateX(20%); } 100% { width: 100%; transform: translateX(100%); } }
      `}</style>
    </div>
  );
};

// Constante de módulo: calculada una sola vez, no en cada render ni en cada handler
const API_URL = import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

const LastActivity = ({ activities, userProfile, dashboardMode = 'essential' }) => {
  // Deep Linking: Recuperar actividad y pestaña desde la URL si están presentes
  const getInitialActivityId = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const paramAct = params.get('activity');
      if (paramAct && activities) {
        const found = activities.find(a => String(a.id) === String(paramAct));
        if (found) return found.id;
      }
    } catch (_) {
      // ignore invalid search params
    }
    return activities?.[0]?.id;
  };

  const getInitialTab = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const paramTab = params.get('tab');
      if (['coach', 'telemetry', 'zones'].includes(paramTab)) {
        return paramTab;
      }
    } catch (_) {
      // ignore invalid search params
    }
    return 'coach';
  };

  const [selectedId, setSelectedId] = React.useState(getInitialActivityId);
  const [aiAnalysis, setAiAnalysis] = React.useState(null);
  const [loadingAi, setLoadingAi] = React.useState(false);
  const [errorAi, setErrorAi] = React.useState(null);
  
  const [stravaDetails, setStravaDetails] = React.useState(null);
  const [loadingStravaDetails, setLoadingStravaDetails] = React.useState(false);
  const [showStravaDetails, setShowStravaDetails] = React.useState(false);
  const [showAiPanel, setShowAiPanel] = React.useState(false);
  const [isDetailedReportOpen, setIsDetailedReportOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(getInitialTab);

  // Sincronizar activeTab cuando el usuario cambie el modo en el dashboard si no hay override por URL
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (!params.get('tab')) {
        setActiveTab('coach');
      }
    } catch (_) {
      // ignore invalid search params
    }
  }, [dashboardMode]);

  // Sincronizar URL para Deep Linking (permite continuar en escritorio sin perder contexto)
  React.useEffect(() => {
    if (!selectedId) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('activity', selectedId);
      url.searchParams.set('tab', activeTab);
      window.history.replaceState(null, '', url.toString());
    } catch (e) {
      console.warn('Error sincronizando URL de actividad:', e);
    }
  }, [selectedId, activeTab]);

  // Modal Focus Reader: soporte de tecla Escape y bloqueo de scroll de fondo
  React.useEffect(() => {
    if (!showAiPanel) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowAiPanel(false);
      }
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAiPanel]);

  // Buscar la actividad seleccionada, fallback a la última si no se encuentra
  const activity = (activities && activities.find(a => a.id === selectedId)) || activities?.[0] || null;

  const suffer_score = activity?.suffer_score;
  const average_heartrate = activity?.average_heartrate;
  const start_date = activity?.start_date;
  const type = activity?.type;
  const sport_type = activity?.sport_type;

  // Carga asíncrona (comprobación inicial en caché o petición de análisis)
  React.useEffect(() => {
    if (!selectedId) return;

    setShowAiPanel(false); // Reset on activity change
    setIsDetailedReportOpen(false);
    let isMounted = true;
    const checkCacheOnly = async () => {
      if (userProfile?.subscriptionTier === 'FREE') {
        if (isMounted) setAiAnalysis(null);
        return;
      }
      setLoadingAi(true);
      setErrorAi(null);
      try {
        const res = await fetch(`${API_URL}/activities/${selectedId}/ai-analyze?checkOnly=true`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (!res.ok) {
          throw new Error(`Error en el servidor: ${res.status}`);
        }
        const data = await res.json();
        if (isMounted) {
          // Si el reporte ya existe en caché, se asigna. Si es null, se mantiene null
          setAiAnalysis(data.analysis || null);
        }
      } catch (err) {
        console.error("Error al comprobar caché de análisis de IA:", err);
        if (isMounted) {
          setAiAnalysis(null);
        }
      } finally {
        if (isMounted) {
          setLoadingAi(false);
        }
      }
    };

    checkCacheOnly();

    return () => {
      isMounted = false;
    };
  }, [selectedId]);

  // Ejecución explícita bajo demanda del usuario
  const handleTriggerAiAnalysis = async (force = false) => {
    if (userProfile?.subscriptionTier === 'FREE') {
      window.dispatchEvent(new CustomEvent('open-pricing-modal'));
      return;
    }
    setLoadingAi(true);
    setErrorAi(null);
    try {
      const url = `${API_URL}/activities/${selectedId}/ai-analyze${force ? '?force=true' : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!res.ok) {
        throw new Error(`Error en el servidor: ${res.status}`);
      }
      const data = await res.json();
      if (data.analysis) {
        setAiAnalysis(data.analysis);
        setIsDetailedReportOpen(true);
      } else {
        setErrorAi('No se recibió el reporte de análisis.');
      }
    } catch (err) {
      console.error("Error al generar análisis de IA:", err);
      setErrorAi('No se pudo conectar con el servicio de análisis de IA. Inténtalo de nuevo.');
    } finally {
      setLoadingAi(false);
    }
  };

  const handleFetchStravaDetails = async () => {
    if (showStravaDetails) {
      setShowStravaDetails(false);
      return;
    }
    setShowStravaDetails(true);
    if (stravaDetails) return; // Ya lo tenemos en caché
    
    setLoadingStravaDetails(true);
    try {
      const res = await fetch(`${API_URL}/activities/${selectedId}/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setStravaDetails(data);
      }
    } catch (err) {
      console.error("Error obteniendo detalles de Strava:", err);
    } finally {
      setLoadingStravaDetails(false);
    }
  };

  // Restablecer detalles al cambiar de actividad
  React.useEffect(() => {
    setStravaDetails(null);
    setShowStravaDetails(false);
  }, [selectedId]);

  // Parseador de Markdown a HTML con soporte para Tablas, Listas, Bloques y Encabezados
  const parseMarkdownToHTML = (md) => {
    if (!md) return '';
    let html = md;

    // 1. Extraer y formatear Tablas Markdown (incluyendo celdas multilínea)
    const lines = html.split('\n');
    const result = [];
    let inTable = false;
    let tableRows = [];

    const isSeparator = (line) => /^\|?\s*:?-+:?\s*(\|?\s*:?-+:?\s*)+\|?$/.test(line.trim());

    const renderTable = (rows) => {
      if (rows.length < 2) return rows.join('\n');
      let headerRow = null;
      const dataRows = [];

      for (let i = 0; i < rows.length; i++) {
        const r = rows[i].trim();
        if (isSeparator(r)) continue;
        const cells = r.split('|').slice(1, -1).map(c => c.trim());
        if (cells.length === 0) continue;
        if (!headerRow) {
          headerRow = cells;
        } else {
          dataRows.push(cells);
        }
      }
      if (!headerRow || dataRows.length === 0) return rows.join('\n');

      let tableHtml = '<div style="overflow-x: auto; margin: 1.25rem 0; border-radius: 10px; border: 1px solid rgba(255,255,255,0.12); background: rgba(0,0,0,0.3);"><table style="width: 100%; border-collapse: collapse; font-size: 0.85rem; text-align: left;"><thead><tr style="background: rgba(255,255,255,0.06); border-bottom: 1px solid rgba(255,255,255,0.12);">';
      headerRow.forEach(h => {
        tableHtml += `<th style="padding: 10px 14px; font-weight: 700; color: #00e5ff; white-space: nowrap;">${h}</th>`;
      });
      tableHtml += '</tr></thead><tbody>';

      dataRows.forEach((row, idx) => {
        const bg = idx % 2 === 1 ? 'background: rgba(255,255,255,0.02);' : '';
        tableHtml += `<tr style="${bg} border-bottom: 1px solid rgba(255,255,255,0.05); transition: background 0.2s;">`;
        row.forEach(cell => {
          tableHtml += `<td style="padding: 10px 14px; color: rgba(255,255,255,0.9); vertical-align: top; line-height: 1.5;">${cell}</td>`;
        });
        tableHtml += '</tr>';
      });

      tableHtml += '</tbody></table></div>';
      return tableHtml;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      if (trimmed.startsWith('|')) {
        inTable = true;
        tableRows.push(trimmed);
      } else if (inTable && tableRows.length > 0 && trimmed.length > 0 && !trimmed.startsWith('#') && !trimmed.startsWith('-') && !trimmed.startsWith('*')) {
        // Línea de continuación dentro de una celda de tabla
        tableRows[tableRows.length - 1] += '<br />' + trimmed;
      } else {
        if (inTable) {
          result.push(renderTable(tableRows));
          tableRows = [];
          inTable = false;
        }
        result.push(line);
      }
    }
    if (inTable && tableRows.length > 0) {
      result.push(renderTable(tableRows));
    }

    html = result.join('\n');

    // 2. Encabezados
    html = html.replace(/^### (.*?)$/gm, '<h3 style="font-size: 1.1rem; font-weight: 800; color: #00e5ff; margin: 1.25rem 0 0.5rem 0; display: flex; align-items: center; gap: 6px;">$1</h3>');
    html = html.replace(/^#### (.*?)$/gm, '<h4 style="font-size: 1rem; font-weight: 700; color: white; margin: 0.75rem 0 0.35rem 0;">$1</h4>');

    // 3. Negritas
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong style="color: white; font-weight: 700;">$1</strong>');

    // 4. Citas estilo Blockquote
    html = html.replace(/^> \*(.*?)\*$/gm, '<div style="background: rgba(139, 92, 246, 0.06); border-left: 3px solid #8b5cf6; border-radius: 4px; padding: 0.75rem 1rem; margin: 1rem 0; font-size: 0.95rem; color: #ebd8ff; font-style: italic;">💡 $1</div>');
    html = html.replace(/^> (.*?)$/gm, '<div style="background: rgba(255,255,255,0.01); border-left: 3px solid var(--glass-border); padding: 0.75rem 1rem; margin: 1rem 0; font-size: 0.95rem; color: rgba(255,255,255,0.85); font-style: italic;">$1</div>');

    // 5. Viñetas y Listas numeradas
    html = html.replace(/^\* (.*?)$/gm, '<li style="margin-left: 1.25rem; list-style-type: disc; margin-bottom: 0.4rem; font-size: 0.95rem;">$1</li>');
    html = html.replace(/^- (.*?)$/gm, '<li style="margin-left: 1.25rem; list-style-type: disc; margin-bottom: 0.4rem; font-size: 0.95rem;">$1</li>');
    html = html.replace(/^(\d+)\. (.*?)$/gm, '<li style="margin-left: 1.25rem; list-style-type: decimal; margin-bottom: 0.4rem; font-size: 0.95rem;">$2</li>');

    // 6. Código
    html = html.replace(/`(.*?)`/g, '<code style="background: rgba(255,255,255,0.08); padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 0.9rem; color: #ff7b47;">$1</code>');

    // 7. Saltos de línea (respetando tablas construidas)
    const parts = html.split(/(<div style="overflow-x: auto;[\s\S]*?<\/div>)/);
    html = parts.map((part, index) => {
      if (index % 2 === 1) return part;
      return part.replace(/\n/g, '<br />');
    }).join('');

    // Sanitizar HTML para evitar XSS
    return DOMPurify.sanitize(html);
  };

  // Formatear fecha legible en español de forma segura
  const safeDateObj = new Date(start_date || new Date());
  const dateFormatted = isNaN(safeDateObj.getTime()) ? 'Fecha desconocida' : safeDateObj.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Emojis de deportes para el select
  const getSportEmoji = (type, name = '') => {
    const t = (type || '').toLowerCase();
    const n = (name || '').toLowerCase();
    if (t.includes('virtualride') || n.includes('rodillo') || n.includes('spinning') || n.includes('ciclo indoor') || n.includes('zwift')) return '⚡';
    if (t.includes('ciclismo') || t.includes('ride') || t.includes('bike') || n.includes('bicicleta') || n.includes('bici')) return '🚴';
    if (t.includes('natación') || t.includes('swim')) return '🏊';
    if (t.includes('pesas') || t.includes('gym') || t.includes('weights') || t.includes('fuerza')) return '💪';
    if (t.includes('andar') || t.includes('walk') || t.includes('hike')) return '🥾';
    return '🏃';
  };

  // Determinar icono del deporte para la vista
  const getSportIcon = (type, name = '') => {
    const t = (type || '').toLowerCase();
    const n = (name || '').toLowerCase();
    if (t.includes('virtualride') || n.includes('rodillo') || n.includes('spinning') || n.includes('ciclo indoor') || n.includes('zwift')) {
      return <Bike size={24} style={{ color: '#a855f7' }} />;
    }
    if (t.includes('ciclismo') || t.includes('ride') || t.includes('bike') || n.includes('bicicleta') || n.includes('bici')) {
      return <Bike size={24} style={{ color: 'var(--chart-endurance)' }} />;
    }
    if (t.includes('natación') || t.includes('swim')) {
      return <Waves size={24} style={{ color: 'var(--chart-water)' }} />;
    }
    if (t.includes('pesas') || t.includes('gym') || t.includes('weights') || t.includes('fuerza')) {
      return <Dumbbell size={24} style={{ color: 'var(--chart-indoor)' }} />;
    }
    return <Flame size={24} style={{ color: 'var(--strava-orange)' }} />;
  };

  // Lógica del Training Effect fisiológico con referencias temporales dinámicas
  const getTrainingEffect = (sufferScore, avgHr, startDate, sportType) => {
    const score = sufferScore || 0;
    const hr = avgHr || 0;
    
    let isToday = false;
    let isYesterday = false;
    
    if (startDate) {
      try {
        const activityDate = new Date(startDate);
        // Verificar que la fecha es válida
        if (!isNaN(activityDate.getTime())) {
          const today = new Date();
          
          const activityDayStr = formatLocalDate(activityDate);
          const todayDayStr = formatLocalDate(today);
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          const yesterdayDayStr = formatLocalDate(yesterday);
          
          if (activityDayStr === todayDayStr) isToday = true;
          if (activityDayStr === yesterdayDayStr) isYesterday = true;
        }
      } catch (err) {
        console.warn("Invalid start_date for getTrainingEffect:", startDate);
      }
    }
    
    const timeRef = isToday ? 'mañana' : (isYesterday ? 'hoy' : 'al día siguiente');

    const t = sportType?.toLowerCase() || '';
    if (t.includes('pesas') || t.includes('gym') || t.includes('indoor') || t.includes('weight') || t.includes('strength') || t.includes('fuerza')) {
      return {
        label: '💪 Entrenamiento de Fuerza',
        desc: 'Sesión enfocada en la hipertrofia, fuerza máxima o resistencia muscular. Genera adaptaciones neuromusculares y daño tisular.',
        tip: 'Coach virtual: ¡Gran trabajo de fuerza! Recuerda consumir suficiente proteína hoy para reparar las fibras musculares.',
        color: '#8b5cf6'
      };
    }

    if (score >= 80 || hr >= 165) {
      return {
        label: '🔥 Capacidad Anaeróbica / VO2 Máx',
        desc: 'Esfuerzo extremo que desarrolla tu velocidad máxima y tu tolerancia al lactato. Estimula tu consumo límite de oxígeno.',
        tip: 'Coach virtual: Necesitas 48h de descanso. Considera paseos suaves o descanso total para asimilar.',
        color: '#ef4444'
      };
    }
    if (score >= 40 || hr >= 148) {
      return {
        label: '⚡ Umbral de Lactato',
        desc: 'Zona productiva aeróbica óptima. Mejora tu ritmo de carrera sostenido y tu capacidad de tolerar el cansancio muscular.',
        tip: `Coach virtual: Requiere 24h de recuperación activa. Considera una caminata ligera o estiramientos ${timeRef}.`,
        color: '#f59e0b'
      };
    }
    if (score >= 15 || hr >= 120) {
      return {
        label: '🟢 Capacidad Aeróbica (Base)',
        desc: 'Favorece tu metabolismo de grasas y fortalece tu capacidad pulmonar y cardíaca general. El motor de tu fitness crónico.',
        tip: `Coach virtual: Buen estímulo de base. ¡Perfecto para seguir construyendo volumen ${timeRef}!`,
        color: '#10b981'
      };
    }
    return {
      label: '🛌 Recuperación Activa',
      desc: 'Sesión sumamente suave orientada a irrigar la musculatura dañada, barrer toxinas metabólicas y disipar la fatiga mental.',
      tip: 'Coach virtual: ¡Gran trabajo regenerativo! Tus fibras están listas para el próximo entreno exigente.',
      color: '#06b6d4'
    };
  };

  const effect = getTrainingEffect(suffer_score, average_heartrate, start_date, type || sport_type);

  // Memoizar la conversión de Markdown: solo se re-ejecuta si aiAnalysis cambia
  const parsedAnalysis = useMemo(() => parseMarkdownToHTML(aiAnalysis), [aiAnalysis]);

  // Extracción del Titular Fisiológico y Pauta 24h del informe IA o métricas deterministas para resumen ejecutivo (15s)
  const executiveSummary = useMemo(() => {
    if (!aiAnalysis) {
      return {
        headline: `${effect.label} — ${effect.desc.split('.')[0]}.`,
        pauta24h: effect.tip.replace(/^Coach virtual:\s*/i, ''),
        isFromAi: false
      };
    }

    // Extraer titular (primer h3 en markdown)
    const h3Match = aiAnalysis.match(/^###\s+(?:\[(.*?)\]|(.*?))$/m);
    let headline = h3Match ? (h3Match[1] || h3Match[2]).trim() : `${effect.label} — Sesión completada con éxito.`;
    headline = headline.replace(/^Titular Sobrio:\s*/i, '').replace(/^\[|\]$/g, '').trim();

    // Extraer pauta 24h
    const pautaMatch = aiAnalysis.match(/\*\s+\*\*Pauta(?: preliminar)? 24h:\*\*\s*([^\n\r]+)/i);
    const pauta24h = pautaMatch ? pautaMatch[1].trim() : effect.tip.replace(/^Coach virtual:\s*/i, '');

    return {
      headline,
      pauta24h,
      isFromAi: true
    };
  }, [aiAnalysis, effect]);

  if (!activities || activities.length === 0 || !activity) return null;

  const { name, distance, moving_time, max_heartrate } = activity;

  // Formatear distancia
  const distanceFormatted = distance ? (distance / 1000).toFixed(2) : '0.00';

  // Formatear duración
  const formatTime = (secs) => {
    if (!secs) return '00:00';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 
      ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      : `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Determinar ritmo o velocidad
  const hasSpeed = activity.average_speed > 0;
  const isCycling = (activity.sport_type || activity.type || '').toLowerCase().match(/ride|bike|ciclismo/);
  const isRunning = Boolean((activity.sport_type || activity.type || '').toLowerCase().match(/run|carrera|running/));
  
  const getPaceOrSpeedLabel = () => {
    if (!hasSpeed) return '--';
    if (isCycling) {
      return <>{(activity.average_speed * 3.6).toFixed(1)} <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-secondary)' }}>km/h</span></>;
    }
    // Ritmo de carrera (min/km)
    const paceSecs = 1000 / activity.average_speed;
    const mins = Math.floor(paceSecs / 60);
    const secs = Math.floor(paceSecs % 60);
    if (mins > 50 || isNaN(mins)) return '--';
    return <>{mins}:{secs.toString().padStart(2, '0')} <span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-secondary)' }}>/km</span></>;
  };

  // Calcular Eficiencia Aeróbica (EF) y Cardiac Drift (cardiovascular drift) estilo Intervals.icu
  const durationMin = moving_time / 60;
  let aerobicEfficiency = null;
  let cardiacDrift = null;
  let driftStatus = null;

  if (average_heartrate > 0 && hasSpeed) {
    if (isCycling) {
      // EF para ciclismo = Velocidad en km/h / HR medio
      const speedKmh = activity.average_speed * 3.6;
      aerobicEfficiency = (speedKmh / average_heartrate).toFixed(2);
    } else {
      // EF para carrera = Ritmo en m/min / HR medio
      const speedMMin = activity.average_speed * 60;
      aerobicEfficiency = (speedMMin / average_heartrate).toFixed(2);
    }

    if (durationMin >= 15) {
      if (activity.aerobicDecoupling !== undefined && activity.aerobicDecoupling !== null) {
        cardiacDrift = activity.aerobicDecoupling;
      } else if (Array.isArray(activity.splits_metric) && activity.splits_metric.length >= 4) {
        // Cálculo canónico comparando primera vs segunda mitad de los parciales
        const validSplits = activity.splits_metric.filter(s => (s.average_speed || 0) > 0 && (s.average_heartrate || 0) > 40);
        if (validSplits.length >= 4) {
          const half = Math.floor(validSplits.length / 2);
          const firstHalf = validSplits.slice(0, half);
          const secondHalf = validSplits.slice(half);

          const ef1 = (firstHalf.reduce((acc, s) => acc + s.average_speed, 0) / firstHalf.length) / 
                      (firstHalf.reduce((acc, s) => acc + s.average_heartrate, 0) / firstHalf.length);
          const ef2 = (secondHalf.reduce((acc, s) => acc + s.average_speed, 0) / secondHalf.length) / 
                      (secondHalf.reduce((acc, s) => acc + s.average_heartrate, 0) / secondHalf.length);

          if (ef1 > 0) {
            cardiacDrift = parseFloat((((ef1 - ef2) / ef1) * 100).toFixed(1));
          }
        }
      }

      if (cardiacDrift === null) {
        // Fallback analítico determinista si no hay splits
        const timeFactor = Math.log(durationMin / 15) * 2.8;
        const hrFactor = average_heartrate > 150 ? 1.2 : 0.9;
        cardiacDrift = parseFloat(Math.max(0.5, timeFactor * hrFactor).toFixed(1));
      }

      // Clasificación del Desacoplamiento (Intervals.icu / Friel)
      if (cardiacDrift < 5.0) {
        driftStatus = { text: 'Excelente (<5%) 💚', color: '#10b981', desc: 'Base aeróbica sólida. Desacople mínimo entre trabajo y respuesta cardíaca.' };
      } else if (cardiacDrift < 8.5) {
        driftStatus = { text: 'Moderado (5-8.5%) 💛', color: '#f59e0b', desc: 'Deriva típica. Buena asimilación pero indicios de cansancio o calor.' };
      } else {
        driftStatus = { text: 'Elevado (>8.5%) 🚨', color: '#ef4444', desc: 'Desacoplamiento alto. Deshidratación, estrés térmico o fatiga neuromuscular.' };
      }
    }
  }

  return (
    <div className="premium-glow-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', position: 'relative', overflow: 'hidden' }}>
      
      {/* BACKGROUND GLOW */}
      <div style={{
        position: 'absolute', top: '-50px', right: '-50px', width: '150px', height: '150px',
        borderRadius: '50%', background: `${effect.color}15`, filter: 'blur(30px)', pointerEvents: 'none'
      }} />

      {/* HEADER TÍTULO TARJETA CON DISTINTIVO Y ENLACE STRAVA */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', zIndex: 1 }}>
        <h2 className="text-xl" style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
          <Activity size={20} style={{ color: 'var(--strava-orange)' }} />
          {activity.id === activities[0].id ? 'Último Entrenamiento' : 'Histórico de entrenamientos'}
        </h2>

        {(!activity.source || activity.source === 'strava') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <PoweredByStrava light={true} />
            <ViewOnStravaButton activityId={activity.id} compact={true} />
          </div>
        )}
      </div>

      {/* HEADER CARD */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', zIndex: 1 }}>
        <div style={{
          background: 'rgba(255,255,255,0.04)', border: '1px solid var(--glass-border)',
          borderRadius: '12px', padding: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {getSportIcon(activity.sport_type || activity.type, activity.name)}
        </div>
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={{ position: 'relative' }}>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(Number(e.target.value))}
              style={{
                appearance: 'none',
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: '1.4rem',
                fontWeight: 800,
                fontFamily: 'inherit',
                lineHeight: 1.2,
                padding: '0',
                paddingRight: '1.5rem',
                cursor: 'pointer',
                outline: 'none',
                width: '100%',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }}
            >
              {activities.slice(0, 15).map((act) => {
                const actDateObj = new Date(act.start_date || new Date());
                const actDate = isNaN(actDateObj.getTime()) ? '??' : actDateObj.toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: 'short'
                });
                const distStr = act.distance > 0 ? ` - ${(act.distance / 1000).toFixed(1)} km` : '';
                return (
                  <option key={act.id} value={act.id} style={{ background: '#111827', color: 'white', fontSize: '1rem' }}>
                    {getSportEmoji(act.sport_type || act.type, act.name)} {act.name} ({actDate}{distStr})
                  </option>
                );
              })}
            </select>
            <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'rgba(255,255,255,0.5)' }}>
              <ChevronDown size={20} />
            </div>
          </div>
          <p className="text-muted" style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '0.15rem', fontSize: '0.85rem' }}>
            <Calendar size={14} /> {dateFormatted}
          </p>
        </div>
      </div>
      {/* SELECTOR DE PESTAÑAS PRINCIPALES (BENTO TABS EN CABECERA) */}
      <div style={{
        display: 'flex',
        background: 'rgba(255,255,255,0.03)',
        padding: '4px',
        borderRadius: '14px',
        border: '1px solid var(--glass-border)',
        gap: '4px',
        zIndex: 1,
        marginTop: '0.15rem'
      }}>
        <button
          onClick={() => setActiveTab('coach')}
          style={{
            flex: 1,
            minHeight: '44px',
            padding: '0.6rem 0.85rem',
            borderRadius: '10px',
            border: 'none',
            background: activeTab === 'coach' ? 'linear-gradient(135deg, #fc4c02 0%, #e03e00 100%)' : 'transparent',
            color: activeTab === 'coach' ? '#fff' : 'rgba(255,255,255,0.7)',
            fontWeight: 700,
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '7px',
            cursor: 'pointer',
            boxShadow: activeTab === 'coach' ? '0 2px 10px rgba(252,76,2,0.35)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <BrainCircuit size={17} />
          <span>⚡ Diagnóstico IA</span>
        </button>

        <button
          onClick={() => setActiveTab('telemetry')}
          style={{
            flex: 1,
            minHeight: '44px',
            padding: '0.6rem 0.85rem',
            borderRadius: '10px',
            border: 'none',
            background: activeTab === 'telemetry' ? 'linear-gradient(135deg, #fc4c02 0%, #e03e00 100%)' : 'transparent',
            color: activeTab === 'telemetry' ? '#fff' : 'rgba(255,255,255,0.7)',
            fontWeight: 700,
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '7px',
            cursor: 'pointer',
            boxShadow: activeTab === 'telemetry' ? '0 2px 10px rgba(252,76,2,0.35)' : 'none',
            transition: 'all 0.15s ease'
          }}
        >
          <Zap size={17} />
          <span>📊 Métricas & Curvas</span>
        </button>

        {dashboardMode === 'pro' && (
          <button
            onClick={() => {
              setActiveTab('zones');
              if (!stravaDetails && !loadingStravaDetails) {
                handleFetchStravaDetails();
              }
            }}
            style={{
              flex: 1,
              minHeight: '44px',
              padding: '0.6rem 0.85rem',
              borderRadius: '10px',
              border: 'none',
              background: activeTab === 'zones' ? 'linear-gradient(135deg, #fc4c02 0%, #e03e00 100%)' : 'transparent',
              color: activeTab === 'zones' ? '#fff' : 'rgba(255,255,255,0.7)',
              fontWeight: 700,
              fontSize: '0.88rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '7px',
              cursor: 'pointer',
              boxShadow: activeTab === 'zones' ? '0 2px 10px rgba(252,76,2,0.35)' : 'none',
              transition: 'all 0.15s ease'
            }}
          >
            <BarChart2 size={17} />
            <span>📈 Zonas & Parciales</span>
          </button>
        )}
      </div>

      {/* CONTENIDO SEGÚN LA PESTAÑA ACTIVA */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', zIndex: 1 }}>
        
        {/* PESTAÑA 1: DIAGNÓSTICO IA & COACH */}
        {activeTab === 'coach' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', animation: 'fadeInTab 0.2s ease-out' }}>
            
            {/* FLASH FISIOLÓGICO (15 SEGUNDOS) - PREVIEW EJECUTIVO */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)',
              border: '1px solid rgba(252, 76, 2, 0.28)',
              borderRadius: '14px',
              padding: '1.15rem 1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.9rem',
              boxShadow: '0 4px 20px rgba(0,0,0,0.18)'
            }}>
              {/* Cabecera del Flash */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    background: 'rgba(252, 76, 2, 0.15)',
                    borderRadius: '8px',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    border: '1px solid rgba(252, 76, 2, 0.3)'
                  }}>
                    <Sparkles size={13} style={{ color: 'var(--strava-orange)' }} />
                    <span style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--strava-orange)', letterSpacing: '0.5px' }}>
                      Flash Fisiológico (15s)
                    </span>
                  </div>
                  {executiveSummary.isFromAi ? (
                    <span style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      ● Diagnóstico IA Activo
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
                      ● Resumen Biomecánico
                    </span>
                  )}
                </div>

                {/* Botones de acción rápida */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {aiAnalysis ? (
                    <>
                      <button
                        onClick={() => handleTriggerAiAnalysis(true)}
                        disabled={loadingAi}
                        title="Reanalizar con métricas actualizadas SportsTech"
                        style={{
                          background: 'rgba(252, 76, 2, 0.15)',
                          border: '1px solid rgba(252, 76, 2, 0.35)',
                          color: '#ff7a45',
                          borderRadius: '16px',
                          padding: '0.4rem 0.85rem',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          cursor: loadingAi ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          transition: 'all 0.2s'
                        }}
                      >
                        <RefreshCw size={12} className={loadingAi ? 'animate-spin' : ''} />
                        <span>Reanalizar</span>
                      </button>
                      <button
                        onClick={() => setShowAiPanel(true)}
                        style={{
                          background: 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          color: 'white',
                          borderRadius: '16px',
                          padding: '0.4rem 0.85rem',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                      >
                        <Eye size={13} style={{ color: 'var(--strava-orange)' }} />
                        <span>Modo Enfoque</span>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleTriggerAiAnalysis(false)}
                      disabled={loadingAi}
                      style={{
                        background: 'linear-gradient(135deg, #fc4c02 0%, #e03e00 100%)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '16px',
                        padding: '0.4rem 0.85rem',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: loadingAi ? 'wait' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        boxShadow: '0 2px 8px rgba(252,76,2,0.3)'
                      }}
                    >
                      {loadingAi ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} />}
                      <span>{loadingAi ? 'Analizando...' : 'Generar Diagnóstico IA'}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Titular Ejecutivo */}
              <div style={{ fontSize: '1.02rem', fontWeight: 700, color: 'white', lineHeight: 1.4 }}>
                {executiveSummary.headline}
              </div>

              {/* Grid de 3 Badges Clave */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '0.65rem'
              }}>
                {/* Badge 1: Carga Real y Aporte */}
                <div style={{
                  background: 'rgba(0,0,0,0.25)',
                  borderRadius: '10px',
                  padding: '0.65rem 0.85rem',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}>
                  <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>
                    Carga Fisiológica
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--strava-orange)', marginTop: '2px' }}>
                    {activity.tss ? `${activity.tss} TSS` : (suffer_score ? `${suffer_score} pts Carga` : 'Carga Moderada')}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', marginTop: '1px' }}>
                    {activity.tss > 100 ? 'Alta fatiga aguda (ATL)' : activity.tss > 50 ? 'Estímulo asimilable' : 'Impacto aeróbico controlado'}
                  </div>
                </div>

                {/* Badge 2: Intensidad / Eficiencia */}
                <div style={{
                  background: 'rgba(0,0,0,0.25)',
                  borderRadius: '10px',
                  padding: '0.65rem 0.85rem',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}>
                  <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>
                    {activity.intensityFactor ? 'Factor de Intensidad' : (aerobicEfficiency ? 'Eficiencia Aeróbica' : 'Carácter de Esfuerzo')}
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 800, color: '#f59e0b', marginTop: '2px' }}>
                    {activity.intensityFactor ? `IF ${activity.intensityFactor}` : (aerobicEfficiency ? `${aerobicEfficiency} ${isCycling ? 'km/h/ppm' : 'm/min/ppm'}` : effect.label.replace(/^.*?\s/, ''))}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', marginTop: '1px' }}>
                    {activity.intensityFactor ? (activity.intensityFactor < 0.75 ? 'Resistencia aeróbica Z2' : activity.intensityFactor < 0.88 ? 'Tempo / Ritmo sostenido' : 'Zona umbral / Intensa') : 'Respuesta metabólica'}
                  </div>
                </div>

                {/* Badge 3: Pauta preliminar 24h */}
                <div style={{
                  background: 'rgba(0,0,0,0.25)',
                  borderRadius: '10px',
                  padding: '0.65rem 0.85rem',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}>
                  <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', color: '#00e5ff', fontWeight: 700 }}>
                    Pauta Preliminar 24h
                  </div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)', marginTop: '3px', lineHeight: 1.35 }}>
                    {executiveSummary.pauta24h}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>
                    Confirmación final en Morning Briefing
                  </div>
                </div>
              </div>
            </div>

            {/* INFORME TÉCNICO FISIOLÓGICO IN-LINE COMPLETO */}
            {loadingAi ? (
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(252, 76, 2, 0.3)',
                borderRadius: '14px',
                padding: '2.5rem 1.5rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                color: 'white'
              }}>
                <RefreshCw size={26} className="animate-spin" style={{ color: '#fc4c02' }} />
                <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>Analizando telemetría y generando diagnóstico con IA...</span>
              </div>
            ) : errorAi ? (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '14px',
                padding: '1.25rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px'
              }}>
                <span style={{ color: '#fca5a5', fontSize: '0.9rem' }}>⚠️ {errorAi}</span>
                <button
                  onClick={() => handleTriggerAiAnalysis(false)}
                  style={{
                    background: '#ef4444', color: 'white', border: 'none',
                    padding: '0.45rem 0.9rem', borderRadius: '8px', fontWeight: 700, cursor: 'pointer'
                  }}
                >
                  Reintentar
                </button>
              </div>
            ) : aiAnalysis ? (
              <div style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '14px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                transition: 'all 0.2s ease'
              }}>
                {/* Cabecera desplegable / Acordeón */}
                <div 
                  onClick={() => setIsDetailedReportOpen(prev => !prev)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsDetailedReportOpen(prev => !prev); } }}
                  role="button"
                  tabIndex={0}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.9rem 1.25rem',
                    cursor: 'pointer',
                    background: isDetailedReportOpen ? 'rgba(255,255,255,0.03)' : 'transparent',
                    borderBottom: isDetailedReportOpen ? '1px solid rgba(255,255,255,0.08)' : 'none',
                    transition: 'background 0.2s ease',
                    userSelect: 'none',
                    flexWrap: 'wrap',
                    gap: '10px'
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                  onMouseOut={e => e.currentTarget.style.background = isDetailedReportOpen ? 'rgba(255,255,255,0.03)' : 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'rgba(252, 76, 2, 0.12)',
                      border: '1px solid rgba(252, 76, 2, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <BrainCircuit size={17} style={{ color: '#fc4c02' }} />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '0.88rem', fontWeight: 800, color: 'white', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Informe Fisiológico Detallado
                        </span>
                        <span style={{ 
                          fontSize: '0.68rem', 
                          padding: '2px 6px', 
                          borderRadius: '6px', 
                          background: 'rgba(252,76,2,0.15)', 
                          color: '#fc4c02', 
                          fontWeight: 700 
                        }}>
                          IA PRO
                        </span>
                      </div>
                      <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                        {isDetailedReportOpen 
                          ? `${activity.name} • Toca para plegar` 
                          : 'Desglose exhaustivo de umbrales, frecuencia cardíaca y fatiga'}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', color: '#fc4c02', fontWeight: 700 }}>
                    <span>{isDetailedReportOpen ? 'Plegar informe' : 'Leer informe completo'}</span>
                    {isDetailedReportOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
                
                {/* Contenido expandible del informe */}
                {isDetailedReportOpen && (
                  <div 
                    className="fade-in"
                    style={{
                      padding: '1.25rem 1.5rem',
                      fontSize: '0.96rem',
                      color: 'rgba(255, 255, 255, 0.92)',
                      lineHeight: 1.75
                    }}
                    dangerouslySetInnerHTML={{ __html: parsedAnalysis }}
                  />
                )}
              </div>
            ) : (
              <div style={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px dashed rgba(252, 76, 2, 0.35)',
                borderRadius: '14px',
                padding: '2rem 1.5rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                textAlign: 'center'
              }}>
                <div style={{ padding: '12px', background: 'rgba(252,76,2,0.15)', borderRadius: '50%', color: '#fc4c02' }}>
                  <Sparkles size={24} />
                </div>
                <div>
                  <h4 style={{ color: 'white', margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Diagnóstico Fisiológico Detallado</h4>
                  <p style={{ color: 'rgba(255,255,255,0.6)', margin: '4px 0 0', fontSize: '0.84rem', maxWidth: '420px' }}>
                    Genera un análisis experto con IA de tu frecuencia cardíaca, umbrales ventilatorios y asimilación de carga.
                  </p>
                </div>
                <button
                  onClick={() => handleTriggerAiAnalysis(false)}
                  disabled={loadingAi}
                  style={{
                    background: 'linear-gradient(135deg, #fc4c02 0%, #e03e00 100%)',
                    border: 'none',
                    color: 'white',
                    borderRadius: '20px',
                    padding: '0.55rem 1.4rem',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 15px rgba(252,76,2,0.3)',
                    marginTop: '4px'
                  }}
                >
                  <Sparkles size={14} />
                  <span>Generar Diagnóstico IA</span>
                </button>
              </div>
            )}

            {/* TRAINING EFFECT AND COACH TIP */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', background: 'rgba(255,255,255,0.015)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '1.25rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <p style={{ fontSize: '1.1rem', fontWeight: 700, color: effect.color, display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
                  <Trophy size={18} /> {effect.label}
                </p>
                <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, margin: '4px 0 0' }}>
                  {effect.desc}
                </p>
                <div style={{
                  marginTop: '0.75rem', background: `${effect.color}0e`, border: `1px solid ${effect.color}25`,
                  borderRadius: '10px', padding: '1rem', fontSize: '0.9rem', color: 'var(--text-primary)',
                  fontWeight: 500, lineHeight: 1.5
                }}>
                  {effect.tip}
                </div>
              </div>
            </div>

            {/* DAILY FEEDBACK */}
            <DailyFeedbackWidget date={activity.start_date.split('T')[0]} />
          </div>
        )}

        {/* PESTAÑA 2: MÉTRICAS Y CURVAS DE TELEMETRÍA */}
        {activeTab === 'telemetry' && (
          <div className="tab-telemetry-container">
            {/* BENTO BOX INMUTABLE DE MÉTRICAS */}
            <UnifiedActivityCard 
              activity={activity} 
              metrics={{
                tss: activity.tss || suffer_score,
                sufferScore: suffer_score,
                normalizedPower: activity.normalizedPower,
                intensityFactor: activity.intensityFactor,
                aerobicEfficiency,
                mechanicalLoad: activity.mechanicalLoad,
                cardiacDrift,
                driftStatus,
                effect
              }}
            />

            {/* GRID DUAL EN DESKTOP: CURVA MMP Y W'BAL */}
            <div className="tab-telemetry-grid">
              <PowerCurveWidget activityId={activity.id} weightKg={userProfile?.weight || 70} />

              {(isCycling || activity.normalizedPower || activity.wPrimeBalMin) && (
                <WPrimeWidget 
                  activityId={activity.id} 
                  initialWBalMin={activity.wPrimeBalMin} 
                  initialWBalExpended={activity.wPrimeExpended} 
                />
              )}
            </div>

            {/* EFICIENCIA AERÓBICA Y DERIVA */}
            {aerobicEfficiency && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem',
                background: 'rgba(255,255,255,0.015)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '1rem'
              }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <TrendingUp size={14} style={{ color: '#00e5ff' }} /> Eficiencia Aeróbica (EF)
                    </span>
                    <span style={{ fontSize: '1.05rem', fontWeight: 800, color: '#00e5ff' }}>
                      {aerobicEfficiency} <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-secondary)' }}>{isCycling ? 'km/h/ppm' : 'm/min/ppm'}</span>
                    </span>
                  </div>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.4, margin: 0 }}>
                    Relación velocidad-pulso. A mayor valor, más velocidad generas por cada latido cardíaco.
                  </p>
                </div>

                {cardiacDrift && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Activity size={14} style={{ color: driftStatus.color }} /> Desacoplamiento (Drift Cardíaco)
                      </span>
                      <span style={{ fontSize: '1.05rem', fontWeight: 800, color: driftStatus.color }}>
                        {cardiacDrift}%
                      </span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.4, margin: 0 }}>
                      <strong>{driftStatus.text}:</strong> {driftStatus.desc}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* CARGA FISIOLÓGICA DE LA SESIÓN */}
            {(activity.tss > 0 || suffer_score > 0) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', background: 'rgba(255,255,255,0.015)', border: '1px solid var(--glass-border)', borderRadius: '12px', padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Zap size={14} style={{ color: 'var(--strava-orange)' }} /> {activity.tss ? 'Carga Fisiológica (TSS Coggan)' : 'Carga de Sesión Estimada'}
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--strava-orange)' }}>
                    {activity.tss ? `${activity.tss} TSS` : `${suffer_score} pts`}
                  </span>
                </div>
                <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${Math.min(((activity.tss || suffer_score) / 120) * 100, 100)}%`,
                    background: 'linear-gradient(90deg, var(--strava-orange), #ff7b47)', borderRadius: '3px'
                  }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* PESTAÑA 3: ZONAS Y PARCIALES */}
        {activeTab === 'zones' && (
          <div className="tab-zones-container">
            {/* BENTO BOX INMUTABLE DE MÉTRICAS */}
            <UnifiedActivityCard 
              activity={activity} 
              metrics={{
                tss: activity.tss || suffer_score,
                sufferScore: suffer_score,
                normalizedPower: activity.normalizedPower,
                intensityFactor: activity.intensityFactor,
                aerobicEfficiency,
                mechanicalLoad: activity.mechanicalLoad,
                cardiacDrift,
                driftStatus,
                effect
              }}
            />

            <div className="tab-zones-grid">
              {/* DISTRIBUCIÓN DE INTENSIDAD EN ZONAS COGGAN / CARDIO */}
              <ZonesDistributionWidget 
                powerZonesTime={activity.powerZonesTime} 
                hrZonesTime={activity.hrZonesTime} 
              />

              {/* BOTÓN Y SECCIÓN DE PARCIALES Y DETALLES DE STRAVA */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'white', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Navigation size={16} style={{ color: 'var(--strava-orange)' }} /> Vueltas y Tiempos de Strava
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {(!activity.source || activity.source === 'strava') && (
                      <ViewOnStravaButton activityId={activity.id} compact={true} />
                    )}
                    <button
                      onClick={handleFetchStravaDetails}
                      disabled={loadingStravaDetails}
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: '8px',
                        minHeight: '38px',
                        padding: '0.4rem 0.85rem',
                        color: 'var(--text-primary)',
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        cursor: loadingStravaDetails ? 'wait' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s'
                      }}
                    >
                      {loadingStravaDetails ? (
                        <><RefreshCw size={13} className="animate-spin" /> Sincronizando vueltas...</>
                      ) : showStravaDetails ? (
                        <><BarChart2 size={13} /> Ocultar Parciales</>
                      ) : (
                        <><BarChart2 size={13} /> 🔄 Recargar Parciales</>
                      )}
                    </button>
                  </div>
                </div>

                {(showStravaDetails || stravaDetails) && (
                  <ActivityDetails details={stravaDetails} />
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* MODAL FOCUS READER (POP-UP CENTRADO CON FONDO DESENFOCADO VIA PORTAL) */}
      {showAiPanel && aiAnalysis && typeof document !== 'undefined' && createPortal(
        <div 
          onClick={() => setShowAiPanel(false)}
          className="ai-focus-modal-overlay"
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="ai-focus-modal-card"
          >
            {/* CABECERA FIJA DEL MODAL */}
            <div className="ai-focus-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                <div style={{ padding: '8px', background: 'rgba(252, 76, 2, 0.15)', border: '1px solid rgba(252, 76, 2, 0.3)', borderRadius: '10px', flexShrink: 0 }}>
                  <BrainCircuit size={22} style={{ color: '#fc4c02' }} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <h2 className="ai-focus-modal-title" style={{ color: 'white', margin: 0, fontWeight: 800 }}>
                      Análisis IA SportsTech
                    </h2>
                    <span style={{
                      fontSize: '0.62rem', fontWeight: 700, background: 'rgba(252, 76, 2, 0.2)',
                      color: '#ff7a45', border: '1px solid rgba(252, 76, 2, 0.4)', borderRadius: '6px',
                      padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap'
                    }}>
                      Informe Fisiológico
                    </span>
                  </div>
                  <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {activity.name} • {dateFormatted}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <button
                  onClick={() => handleTriggerAiAnalysis(true)}
                  disabled={loadingAi}
                  title="Reanalizar con métricas avanzadas SportsTech"
                  className="ai-focus-refresh-btn"
                >
                  <RefreshCw size={15} className={loadingAi ? 'animate-spin' : ''} />
                  <span className="hide-on-mobile">Reanalizar</span>
                </button>

                <button
                  onClick={() => setShowAiPanel(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    width: '38px',
                    height: '38px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  title="Cerrar (Esc)"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* CUERPO DEL MODAL (LECTURA CON SCROLL INDEPENDIENTE) */}
            <div 
              className="ai-focus-modal-body"
              dangerouslySetInnerHTML={{ __html: parsedAnalysis }}
            />
          </div>
        </div>,
        document.body
      )}

      {/* CSS PARA LA TARJETA */}
      <style>{`
        .ai-dynamic-card {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        }
        .ai-mesh-bg-orange {
          position: absolute;
          top: -50%; left: -50%; right: -50%; bottom: -50%;
          background: radial-gradient(circle at 50% 50%, #fc4c02 0%, #ff0055 40%, #8b5cf6 80%, #0f172a 100%);
          opacity: 0.95;
          z-index: 0;
          animation: meshPulseOrange 8s ease-in-out infinite alternate;
        }
        @keyframes meshPulseOrange {
          0% { transform: scale(1) translate(0, 0); }
          50% { transform: scale(1.1) translate(2%, 2%); }
          100% { transform: scale(1) translate(-2%, -2%); }
        }
        @keyframes fadeInTab {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleInModal {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .ai-focus-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          inset: 0;
          width: 100vw;
          height: 100vh;
          height: 100dvh;
          background: rgba(0, 0, 0, 0.82);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          z-index: 9999999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          box-sizing: border-box;
          animation: fadeInOverlay 0.2s ease-out;
        }
        .ai-focus-modal-card {
          background: #0f172a;
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 20px;
          width: 100%;
          max-width: 880px;
          height: 86vh;
          max-height: 900px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.8), 0 0 35px rgba(252, 76, 2, 0.12);
          overflow: hidden;
          animation: scaleInModal 0.25s cubic-bezier(0.16, 1, 0.3, 1);
          box-sizing: border-box;
        }
        .ai-focus-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.75rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          flex-shrink: 0;
          gap: 12px;
          box-sizing: border-box;
        }
        .ai-focus-modal-title {
          font-size: 1.2rem;
        }
        .ai-focus-refresh-btn {
          background: rgba(252, 76, 2, 0.15);
          border: 1px solid rgba(252, 76, 2, 0.4);
          border-radius: 8px;
          padding: 0.5rem 0.9rem;
          cursor: pointer;
          color: #ff7a45;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.82rem;
          font-weight: 700;
          min-height: 38px;
          transition: all 0.15s ease;
        }
        .ai-focus-modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 2rem 2.25rem;
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.92);
          line-height: 1.7;
          -webkit-overflow-scrolling: touch;
          box-sizing: border-box;
        }
        @media (max-width: 768px) {
          .ai-focus-modal-overlay {
            padding: 0 !important;
          }
          .ai-focus-modal-card {
            border: none !important;
            border-radius: 0 !important;
            max-width: 100vw !important;
            width: 100vw !important;
            height: 100vh !important;
            height: 100dvh !important;
            max-height: 100dvh !important;
            padding-top: env(safe-area-inset-top, 0) !important;
            padding-bottom: env(safe-area-inset-bottom, 0) !important;
          }
          .ai-focus-modal-header {
            padding: 0.8rem 1rem !important;
            gap: 8px !important;
          }
          .ai-focus-modal-title {
            font-size: 1.02rem !important;
          }
          .ai-focus-refresh-btn {
            padding: 0 !important;
            width: 38px !important;
            height: 38px !important;
            justify-content: center !important;
          }
          .hide-on-mobile {
            display: none !important;
          }
          .ai-focus-modal-body {
            padding: 1.1rem 1.1rem calc(2rem + env(safe-area-inset-bottom, 0)) !important;
            font-size: 0.94rem !important;
          }
        }
        .tab-telemetry-container, .tab-zones-container {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          animation: fadeInTab 0.2s ease-out;
        }
        .tab-telemetry-grid, .tab-zones-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
          align-items: start;
        }
        @media (min-width: 1024px) {
          .tab-telemetry-grid {
            grid-template-columns: 1.12fr 0.88fr;
          }
          .tab-zones-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
        .generate-ai-btn:active {
          transform: scale(0.95);
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LastActivity;
